import type { Table } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";

import {
  Table as TableUI,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { LIMITED_DATA_SUMMARY } from "@/constants/dataset";
import { MESSAGE } from "@/constants/message";

import { twMerge } from "tailwind-merge";

interface TableDataProps<TData> {
  readonly table: Table<TData>;
  readonly limit?: number;
}

function TransposedTable<TData>({
  table,
  limit = LIMITED_DATA_SUMMARY.TABLE
}: TableDataProps<TData>) {
  const totalRecords = table.getRowModel().rows?.length;
  if (!totalRecords)
    return (
      <div className="flex-center h-[400px] px-4">
        <p className="text-base text-txtHeading font-semibold text-center">
          {MESSAGE.ANALYSIS_SUMMARY.NO_DATA_ANALYSIS_SUMMARY_DATASET}
        </p>
      </div>
    );
  return (
    <div className="h-full w-full rounded-lg overflow-y-hidden border border-gainsboro overflow-x-auto scrollbar-overlay">
      <TableUI className="w-full min-w-max text-xs">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup, index) => (
            <TableRow key={`${headerGroup.id}-${index}`}>
              {headerGroup.headers.map((header, index) => {
                return (
                  <TableHead
                    key={`${header.id}-${index}`}
                    className={twMerge(
                      "h-[50px] bg-white-smoke py-2 px-3 font-semibold",
                      index === 0 ? "max-w-[202px]" : "text-right"
                    )}
                    scope="col"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="h-[250px]">
          {table
            .getRowModel()
            .rows.slice(0, limit)
            .map((row, rowIndex) => {
              return (
                <TableRow
                  key={`${row.id}-${rowIndex}`}
                  className="h-[52px] last:border-b-0"
                  data-state={row.getIsSelected() ? "selected" : undefined}
                  aria-selected={row.getIsSelected()}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    const content = flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    );
                    return (
                      <TableCell
                        key={`${cell.id}-${cellIndex}`}
                        className={twMerge(
                          "py-2 px-3 last:border-b-0",
                          cellIndex !== 0 ? "text-right" : "max-w-[202px]"
                        )}
                      >
                        <div>{content}</div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
        </TableBody>
      </TableUI>
    </div>
  );
}

export default TransposedTable;
